<template>
  <div>
    <p class="mt-2">
      {{ t('warehouse.pages.settings.messages.enableToSelectAndOrderDetailsInAppointmentTile') }}
    </p>

    <v-switch v-model="enabled" color="secondary" density="compact" :disabled="readOnly">
      <template v-slot:label>
        <div :class="{ 'text--disabled': !enabled }">
          <div class="pl-1">
            <span>
              {{
                enabled
                  ? t('warehouse.pages.settings.messages.enabledShowingCustomGrid')
                  : t('warehouse.pages.settings.messages.disabledShowingDefaultGrid')
              }}
            </span>
          </div>
        </div>
      </template>
    </v-switch>

    <p :class="{ 'text--disabled': !enabled }">
      {{
        t(
          'warehouse.pages.settings.messages.selectAndOrderDetailsYouWantToDisplayInEachAppointmentTile'
        )
      }}:
    </p>

    <v-alert border="start" type="info" v-if="enabled && lastItem">
      {{ t('warehouse.pages.settings.messages.youMustKeepAtLeastOneItemSelected') }}
    </v-alert>

    <!-- ACTIVE ITEMS: -->
    <div id="datablock-list">
      <!-- `visibleKeys`, not `config`: a key with no data block has no label to draw. `ltcf:`
           blocks arrive with the load-type fetch, so a saved one is in `config` before
           `dataBlocks` has it. Reordering maps back through the same list. -->
      <template v-for="key in visibleKeys" :key="key">
        <v-checkbox
          v-model="config"
          :value="key"
          hide-details
          :disabled="readOnly || !enabled || lastItem">
          <template v-slot:prepend>
            <v-icon class="handle">mdi-arrow-split-horizontal</v-icon>
          </template>
          <template v-slot:label>
            <grid-tile-data-block-checkbox-label :item="dataBlocks[key]" :disabled="!enabled" />
          </template>
        </v-checkbox>
      </template>
    </div>

    <!-- INACTIVE ITEMS: -->
    <div>
      <v-checkbox
        v-for="(item, key) in dataBlocks"
        v-model="config"
        :key="key"
        :value="key"
        v-show="!config.includes(key)"
        hide-details
        :disabled="readOnly || !enabled">
        <template v-slot:prepend>
          <v-icon class="handle" disabled>mdi-arrow-split-horizontal</v-icon>
        </template>
        <template v-slot:label>
          <grid-tile-data-block-checkbox-label :item="item" :disabled="!enabled" />
        </template>
      </v-checkbox>
    </div>

    <p class="mt-8 font-size-small">
      <span class="font-weight-bold">{{ t('global.basicTerms.notes') }}:</span>
      {{ t('warehouse.pages.settings.messages.changesWillBeAppliedToPastAndFutureAppointments') }}
    </p>
  </div>
</template>

<script>
import { computed } from 'vue';
import Sortable from 'sortablejs';
import { cloneDeep, isArray, isString } from 'lodash';
import {
  buildGridTileLoadTypeCustomFormFieldKey,
  CustomFieldType,
  CustomFormsFeatures,
  isGridTileLoadTypeCustomFormFieldKey,
  t
} from '@nova/core';
import customFormsService from '@satellite/services/custom-forms-service';
import useLimiter from '@satellite/composables/useLimiter';

export default {
  name: 'GridTileConfigEditor',
  setup(props) {
    const warehouseSettings = computed(() => props.warehouse?.settings);
    const { shouldShowHandlingUnits, handlingUnitsLabel } = useLimiter(warehouseSettings);

    return {
      shouldShowHandlingUnits,
      handlingUnitsLabel
    };
  },
  props: {
    inputEnabled: {
      type: Boolean,
      required: true
    },
    inputConfig: {
      type: Array,
      required: true
    },
    warehouse: {
      type: Object,
      required: true
    },
    /** Disables the switch, checkboxes and drag. Default false keeps the legacy page unchanged. */
    readOnly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      // Seeded here, not in `mounted`: assigning it there fires the watcher below, whose select-all
      // then fills an empty config on every mount. This panel is lazy, so that was once per expand.
      enabled: this.inputEnabled,
      sortable: null,
      isUnmounted: false,
      config: [],
      dataBlocks: {
        company_name: {
          title: this.t('warehouse.pages.settings.labels.carrierCompanyName')
        },
        start_time: {
          title: this.t('warehouse.pages.settings.labels.startTime')
        },
        tags: {
          title: this.t('warehouse.pages.settings.labels.tags')
        },
        ref_number: {
          title: this.t('warehouse.pages.settings.labels.referenceNumber')
        },
        conf_number: {
          title: this.t('warehouse.pages.settings.labels.confirmationNumber')
        },
        dock_name: {
          title: this.t('warehouse.pages.settings.labels.dockName'),
          note: this.t('warehouse.pages.settings.messages.onlyAvailableForWeekAndMonthViews')
        },
        load_type: {
          title: this.t('warehouse.pages.settings.labels.loadTypeName')
        },
        arrival_time: {
          title: this.t('warehouse.pages.settings.labels.arrivalTime')
        },
        arrival_date: {
          title: this.t('warehouse.pages.settings.labels.arrivalDate')
        }
      }
    };
  },
  mounted() {
    // Add handling units if feature flag and settings are enabled
    if (this.shouldShowHandlingUnits) {
      this.dataBlocks['handling_units'] = {
        title: this.handlingUnitsLabel
      };
    }

    const config = cloneDeep(this.inputConfig);

    this.config = cloneDeep(config);

    const fieldsTmplt = this.warehouse?.customApptFieldsTemplate;

    if (isArray(fieldsTmplt)) {
      for (const field of fieldsTmplt) {
        // TODO: For now, we don't allow Doc/MultiDoc custom field types.
        if (
          field.type !== CustomFieldType.Document &&
          field.type !== CustomFieldType.MultiDocument
        ) {
          this.dataBlocks[`custom:${field.label}`] = {
            title: field.label,
            note: this.t('global.specialtyTerms.customFields')
          };
        }
      }
    }

    // Sync `mounted`: load load-type options first; only drop unknown `ltcf:` keys after a successful load.
    const filterConfig = loadTypeOptionsLoadedOk =>
      config.filter(item => {
        if (isString(item) && item.startsWith('custom:')) {
          return item in this.dataBlocks;
        }
        if (isString(item) && isGridTileLoadTypeCustomFormFieldKey(item)) {
          return loadTypeOptionsLoadedOk ? item in this.dataBlocks : true;
        }
        return true;
      });

    this.loadLoadTypeFormGridTileOptions()
      .then(() => {
        this.config = filterConfig(true);
      })
      .catch(() => {
        this.config = filterConfig(false);
      })
      .finally(() => {
        this.$nextTick(() => this.initSortable());
      });
  },
  computed: {
    lastItem() {
      // Counts what is RENDERED, not the model: a key with no data block cannot be unchecked, so
      // counting it would leave the last visible checkbox clearable and the warning unshown.
      return this.visibleKeys.length === 1;
    },
    visibleKeys() {
      return this.config.filter(key => this.dataBlocks[key]);
    }
  },
  methods: {
    t,
    async loadLoadTypeFormGridTileOptions() {
      const docks = this.warehouse?.docks || [];
      const loadTypeIds = [...new Set(docks.flatMap(d => d.loadTypeIds || []).filter(Boolean))];
      if (!loadTypeIds.length) {
        return;
      }

      // Covers API/network failures from getLoadTypesTriggers. Optional chaining when walking
      // trigger.flow keeps unexpected payload shapes from throwing (defensive, not a substitute for typing).
      try {
        const triggers = await customFormsService.getLoadTypesTriggers(loadTypeIds, {
          feature: CustomFormsFeatures.BookLoadType,
          isActive: true,
          warehouseId: this.warehouse?.id
        });

        const seen = new Set();
        for (const trigger of triggers || []) {
          const formFields = trigger?.flow?.formFrom?.formFields ?? [];
          for (const formField of formFields) {
            const id = formField.id;
            if (!id || seen.has(id)) {
              continue;
            }
            const fieldType = formField.field?.type;
            if (
              fieldType === CustomFieldType.Document ||
              fieldType === CustomFieldType.MultiDocument
            ) {
              continue;
            }
            seen.add(id);
            const key = buildGridTileLoadTypeCustomFormFieldKey(id);
            this.dataBlocks[key] = {
              title: formField.overrideLabel || formField.field?.label || '',
              note: this.t('warehouse.pages.settings.messages.loadTypeCustomFormFieldGridTileNote')
            };
          }
        }
      } catch (error) {
        console.error(
          'GridTileConfigEditor: failed to load load-type form grid tile options',
          error
        );
      }
    },
    initSortable() {
      const _this = this;

      // Scoped to this instance's own subtree, and skipped once it is gone: the fetch above
      // outlives a collapse, and a document-wide lookup would then bind a second Sortable to
      // whichever panel is on screen, with nothing left to destroy it.
      const container = this.isUnmounted ? null : this.$el?.querySelector?.('#datablock-list');
      if (!container) {
        return;
      }

      // Kept so the readOnly watcher can update the instance: initSortable runs once.
      this.sortable = Sortable.create(container, {
        disabled: this.readOnly,
        handle: '.handle',
        animation: 150,
        filter: '.filtered',
        ghostClass: 'ghost-item',
        onEnd({ oldIndex, newIndex }) {
          if (_this.readOnly) {
            return;
          }

          // Sortable's indexes are into the RENDERED list, which omits keys with no data block.
          // Applying them to `config` directly would move the wrong key. The reordered visible
          // keys are poured back into the visible slots, so a hidden key keeps its place.
          const reordered = [..._this.visibleKeys];
          const [movedItem] = reordered.splice(oldIndex, 1);
          if (movedItem === undefined) {
            return;
          }
          reordered.splice(newIndex, 0, movedItem);

          let next = 0;
          _this.config = _this.config.map(key => (_this.dataBlocks[key] ? reordered[next++] : key));
        }
      });
    }
  },
  beforeUnmount() {
    // This editor sits in an expansion panel, so it is unmounted on every collapse.
    this.isUnmounted = true;
    this.sortable?.destroy();
    this.sortable = null;
  },
  watch: {
    readOnly(value) {
      this.sortable?.option('disabled', value);
    },
    config: {
      handler(val) {
        this.$emit('configUpdated', cloneDeep(val));
      },
      deep: true
    },
    enabled(val) {
      if (val) {
        // If the user just switched on the new system, and nothing is selected in the config,
        // then just go ahead and select everything. This way they start out with "all the things".
        if (this.config.length === 0) {
          for (const key of Object.keys(this.dataBlocks)) {
            this.config.push(key);
          }
        }
      }
      this.$emit('enabledUpdated', cloneDeep(val));
    }
  }
};
</script>
